import PropTypes from "prop-types";
import { useState, useEffect, useRef, useCallback } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import _ from "lodash";
import moment from "moment";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import colors from "assets/theme/base/colors";
import Icon from "@mui/material/Icon";
import TimePicker from "@mui/lab/TimePicker";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import SuiDatePicker from "components/SuiDatePicker";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import TextField from "@mui/material/TextField";
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import Switch from '@mui/material/Switch';

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import SuiSelect from "components/SuiSelect";
import SuiBadgeDot from "components/SuiBadgeDot";
import SuiEditor from "components/SuiEditor";
import FormField from "components/FormField";
import MediaPicker from "components/MediaPicker";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { Post, PostType } from "api";

function NewOrEditPost({ mode }) {
  const { t } = useTranslation();
  const { id, postTypeId } = useParams();
  const isEditing = !!id;
  const TITLE = t(isEditing ? "Edit Post" : "New Post");
  const history = useHistory();
  const [postOriginal, setPostOriginal] = useState({
    status: "draft",
  });
  const [post, setPost] = useState({
    status: "draft",
  });
  const [postType, setPostType] = useState({
    status: "draft",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [expandSeo, setExpandSeo] = useState(true);
  const [showHtml, setShowHtml] = useState(false);
  const [currLang, setCurrLang] = useState("SC");
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [isSendPushNotification, setIsSendPushNotification] = useState(false);

  const timePickerRef = useRef(null);
  const [scheduleDate, setScheduleDate] = useState(moment().add(2, "hours").format("YYYY-MM-DD"));
  const [scheduleTime, setScheduleTime] = useState(moment().add(2, "hours").format("HH:mm:00"));

  const getCurrLangLabel = () => {
    if (currLang === "TC") {
      return t("Traditional Chinese");
    }
    if (currLang === "SC") {
      return t("Simplified Chinese");
    }
    return t("English");
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const pt = await PostType.get(postTypeId);
        setPostType(pt.data);
        setIsSendPushNotification(pt.data.isSendPushNotification);
        if (isEditing) {
          const p = await Post.get(id);
          setPost(p.data);
          setPostOriginal(p.data);
        }
        setScheduleDate(moment(p.data["schedulePublishedDate"]).format("YYYY-MM-DD"));
        setScheduleTime(moment(p.data["schedulePublishedDate"]).format("HH:mm:00"));
      } catch (err) {
        console.error(err);
      }
      setIsLoading(false);
    })();
  }, []);

  const readPost = (prop) => post[prop + currLang] || "";
  const readPostOriginal = (prop) => postOriginal[prop + currLang] || "";
  const updatePost = (prop, value) => {
    const n = { ...post };
    if (prop === "css" || prop === "status" || prop === "videoOrHtml" || prop === "isSendPushNotification") {
      n[prop] = value;
    } else {
      n[prop + currLang] = value;
    }
    setPost(n);
  };
  const updatePost2 = (prop, value) => {
    const n = { ...post };
    n[prop] = value;    
    setPost(n);
  };

  const readPostType = (prop) => {
    if (prop === "css" || prop === "status" || prop === "isSendPushNotification") {
      return postType[prop];
    }
    return postType[prop + currLang] || "";
  };
  const editorOnChange = (e) => updatePost("content", e);
  const getPostStatusLabel = () => {
    if (post.status === "3") {
      return "Published";
    }
    if (post.status === "2") {
      return "Scheduled";
    }
    return "Draft";
  };
  const getPostOriginalStatus = () => {
    if (postOriginal.status === "3") {
      return "Published";
    }
    if (postOriginal.status === "2") {
      return "Scheduled";
    }
    return "Draft";
  };
  const getPostOriginalStatusColor = () => {
    if (postOriginal.status === "3") {
      return "success";
    }
    if (postOriginal.status === "2") {
      return "info";
    }
    return "light";
  };
  const getPostStatusOptions = () => {
    if (!isEditing || postOriginal.status === "1") {
      return [
        {
          label: t("Draft"),
          value: "1",
        },
        {
          label: t("Schedule to Publish"),
          value: "2",
        },
        {
          label: t("Publish Now"),
          value: "3",
        },
      ];
    } else if (postOriginal.status === "2") {
      return [
        {
          label: t("Scheduled"),
          value: "2",
        },
        {
          label: t("Draft"),
          value: "1",
        },
        {
          label: t("Published"),
          value: "3",
        },
      ];
    }
    return [
      {
        label: t("Published"),
        value: "3",
      },
    ];
  };
  const postCategoryOptions = useCallback(() => {
    const cat = readPostType("category");
    if (!cat) return [];
    return cat.split(",").map(rr => ({ label: rr, value: rr }));
  }, [postType, currLang]);
  const postSubcategoryOptions = useCallback(() => {
    const cat = readPostType("subCategory");
    if (!cat) return [];
    return cat.split(",").map(rr => ({ label: rr, value: rr }));
  }, [postType, currLang]);

  const save = async () => {
    let error = "";
    if (!isEditing) {
      if (post.title || post.slug) {
        if (!post.title) {
          error = "Missing title";
        } else if (!post.slug) {
          error = "Missing slug";
        }
      } else if (post.titleTC || post.slugTC) {
        if (!post.titleTC) {
          error = "Missing title";
        } else if (!post.slugTC) {
          error = "Missing slug";
        }
      } else if (post.titleSC || post.slugSC) {
        if (!post.titleSC) {
          error = "Missing title";
        } else if (!post.slugSC) {
          error = "Missing slug";
        }
      } else {
        error = "Missing title";
      }
    }
    if (error) {
      Swal.fire(t(error));
    } else {
      try {
        const data = {};
        [
          "id",
          "title",
          "titleTC",
          "titleSC",
          "subTitle",
          "subTitleTC",
          "subTitleSC",
          "slug",
          "slugTC",
          "slugSC",
          "category",
          "categoryTC",
          "categorySC",
          "subCategory",
          "subCategoryTC",
          "subCategorySC",
          "content",
          "contentTC",
          "contentSC",
          "css",
          "video",
          "videoTC",
          "videoSC",
          "videoOrHtml",
          "metaTitle",
          "metaTitleTC",
          "metaTitleSC",
          "metaKeyword",
          "metaKeywordTC",
          "metaKeywordSC",
          "metaDescription",
          "metaDescriptionTC",
          "metaDescriptionSC",
          "status",
          "schedulePublishedDate",
          "isSendPushNotification",
        ].forEach((v) => {
          data[v] = post[v];
        });
        data.postTypeId = postTypeId;
        if (post.status === "2") {
          if (scheduleDate === "Invalid date" || scheduleTime === "Invalid date") {
            Swal.fire(t("Invalid date entered"));
            return;
          }

          data["schedulePublishedDate"] = moment(`${scheduleDate} ${scheduleTime}`).toString();
        }
        if (selectedMedia) {
          data["thumbnail"] = selectedMedia.id;
        }
        if (isEditing) {
          await Post.update(data);
        } else {
          await Post.create(data);
        }
        history.goBack();
      } catch (e) {
        console.error(e);
      }
    }
  };

  const del = async () => {
    const result = await Swal
      .mixin({
        customClass: {
          confirmButton: "button button-success",
          cancelButton: "button button-error",
        },
        buttonsStyling: false,
      })
      .fire({
        title: t("Confirm to delete?"),
        showCancelButton: true,
        confirmButtonText: t("Yes"),
        cancelButtonText: t("No"),
      });
    if (result.isConfirmed) {
      try {
        await Post.del(id);
        history.goBack();
      } catch (e) {
        console.error(e);
      }
    }
  };

  const handleScheduleChange = (date, time) => {
    const result = moment(`${date} ${time}`, "YYYY-MM-DD HH:mm:ss");
    setScheduleDate(moment(result).format("YYYY-MM-DD"));
    setScheduleTime(moment(result).format("HH:mm:00"));
  };

  return (
    <>
      <Helmet>
        <title>{TITLE}</title>
      </Helmet>
      <DashboardLayout>
        <DashboardNavbar />
        <SuiBox my={1.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={9}>
              <Card style={{ overflow: "visible" }}>
                <SuiBox p={3}>
                  <SuiBox>
                    <SuiBox
                      py={1}
                      style={{
                        float: "right",
                        minWidth: 200,
                      }}
                    >
                      <SuiSelect
                        placeholder="Choose a type"
                        value={{
                          label: getCurrLangLabel(),
                          value: currLang,
                        }}
                        options={[
                          {
                            label: t("Traditional Chinese"),
                            value: "TC",
                          },
                          {
                            label: t("Simplified Chinese"),
                            value: "SC",
                          },
                          {
                            label: t("English"),
                            value: "",
                          },
                        ]}
                        onChange={(option) => {
                          setCurrLang(option.value);
                        }}
                      />
                    </SuiBox>
                    <SuiTypography variant="h5">{TITLE}</SuiTypography>
                    <SuiTypography variant="button" fontWeight="regular" color="text">
                      {t(`${isEditing ? "Edit" : "New"} Post`)}
                    </SuiTypography>
                  </SuiBox>

                  <Divider />
                  {(!isEditing || (isEditing && !isLoading)) && (
                    <Grid item>
                      <FormField
                        label={t("title")}
                        disabled={(isEditing && readPostOriginal("title")) || mode === "view"}
                        value={readPost("title")}
                        onChange={(e) => {
                          updatePost("title", e.target.value);
                        }}
                      />
                      {postType.id !== 7 &&
                        <FormField
                          label={t("slug")}
                          disabled={(isEditing && readPostOriginal("slug")) || mode === "view"}
                          value={readPost("slug")}
                          onChange={(e) => {
                            updatePost("slug", e.target.value);
                          }}
                        />
                      }
                      {!!(postType.id === 4) && (
                        <FormField
                          label={t("Marquee")}
                          disabled={mode === "view"}
                          value={readPost("subTitle")}
                          onChange={(e) => {
                            updatePost("subTitle", e.target.value);
                          }}
                        />
                      )}
                      {!!(postType.id === 3 || postType.id === 6) && (
                        <FormField
                          label={t("Sub-title")}
                          disabled={mode === "view"}
                          value={readPost("subTitle")}
                          onChange={(e) => {
                            updatePost("subTitle", e.target.value);
                          }}
                        />
                      )}

                      {!!(postType.id === 8) && (
                        <FormField
                          label={t("Image URL")}                          
                          disabled={mode === "view"}
                          value={post.video}
                          onChange={(e) => {
                            updatePost2("video", e.target.value);
                          }}
                        />
                      )}                     

                      {!!(postType.id === 8) && (
                        <FormField
                          label={t("Display Date (yyyy-MM-dd)")}                          
                          disabled={mode === "view"}
                          value={post.videoTC}
                          onChange={(e) => {
                            updatePost2("videoTC", e.target.value);
                          }}
                        />
                      )}
                      {!!postCategoryOptions().length &&
                        <SuiBox mb={3}>
                          <SuiTypography component="label" variant="caption" fontWeight="bold">
                            {t("Categories")}
                          </SuiTypography>
                          <SuiSelect
                            value={{
                              label: t(readPost("category")),
                              value: "",
                            }}
                            options={postCategoryOptions()}
                            onChange={(option) => {
                              updatePost("category", option.value);
                            }}
                            isDisabled={mode === "view"}
                          />
                        </SuiBox>
                      }
                      {!!(postSubcategoryOptions().length) &&
                        <SuiBox mb={3}>
                          <SuiTypography component="label" variant="caption" fontWeight="bold">
                            {t("Sub-categories")}
                          </SuiTypography>
                          <SuiSelect
                            value={{
                              label: t(readPost("subCategory")),
                              value: "",
                            }}
                            options={postSubcategoryOptions()}
                            onChange={(option) => {
                              updatePost("subCategory", option.value);
                            }}
                            isDisabled={mode === "view"}
                          />
                        </SuiBox>
                      }
                      {[5, 6, 7].indexOf(postType.id) !== -1 &&
                        <MediaPicker onChange={setSelectedMedia} defaultSelectedMediaId={post.thumbnail} />
                      }
                      {postType.id === 5 &&
                        <SuiBox mb={3} mt={4} ml={.5}>
                          <FormControl>
                            <SuiTypography component="label" variant="caption" fontWeight="bold">
                              {t("Content Types")}
                            </SuiTypography>
                            <SuiBox ml={1.5} mt={.5}>
                              <RadioGroup
                                aria-labelledby="demo-radio-buttons-group-label"
                                defaultValue="female"
                                name="radio-buttons-group"
                                row
                              >
                                <FormControlLabel onChange={() => {
                                  updatePost("videoOrHtml", "html");
                                }} value={"html"} control={<Radio checked={post.videoOrHtml === "html"} />} label={<SuiTypography component="label" variant="caption" fontWeight="bold">
                                  {t("HTML Content")}
                                </SuiTypography>}
                                  disabled={mode === "view"}
                                />
                                <SuiBox ml={3}>
                                  <FormControlLabel onChange={() => {
                                    updatePost("videoOrHtml", "video");
                                  }} value={"video"} control={<Radio checked={post.videoOrHtml === "video"} />} label={<SuiTypography component="label" variant="caption" fontWeight="bold">
                                    {t("Video Link")}
                                  </SuiTypography>}
                                    disabled={mode === "view"}
                                  />
                                </SuiBox>
                              </RadioGroup>
                            </SuiBox>
                          </FormControl>
                        </SuiBox>
                      }
                      {(post.videoOrHtml === "video" || postType.id === 7) && (
                        <FormField
                          label={t(postType.id !== "7" ? "Video Link" : "External Link")}
                          disabled={mode === "view"}
                          value={readPost("video")}
                          onChange={(e) => {
                            updatePost("video", e.target.value);
                          }}
                        />
                      )}                      
                      {(post.videoOrHtml === "html" || postType.id !== 5) && postType.id !== 7 && (
                        <>
                          <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                            <SuiTypography component="label" variant="caption" fontWeight="bold">
                              {t("Content")}
                            </SuiTypography>
                            &nbsp;&nbsp;
                            <SuiButton
                              iconOnly
                              onClick={() => {
                                setShowHtml(!showHtml);
                              }}
                            >
                              <Icon sx={{ fontWeight: "bold" }}>data_object</Icon>
                            </SuiButton>
                          </SuiBox>
                          {!showHtml && (
                            <SuiBox mb={3}>
                              {currLang === "" && (
                                <SuiEditor
                                  readOnly={mode === "view"}
                                  value={readPost("content") || ""}
                                  onChange={editorOnChange}
                                />
                              )}
                              {currLang === "TC" && (
                                <SuiEditor
                                  readOnly={mode === "view"}
                                  value={readPost("content") || ""}
                                  onChange={editorOnChange}
                                />
                              )}
                              {currLang === "SC" && (
                                <SuiEditor
                                  readOnly={mode === "view"}
                                  value={readPost("content") || ""}
                                  onChange={editorOnChange}
                                />
                              )}
                            </SuiBox>
                          )}
                          {showHtml && (
                            <FormField
                              lable={<></>}
                              placeholder={t("Enter Content")}
                              multiline
                              minRows={5}
                              disabled={mode === "view"}
                              onChange={(e) => {
                                updatePost("content", e.target.value);
                              }}
                              value={readPost("content")}
                            />
                          )}
                        </>
                      )}
                      {(postType.id !== 7) && (
                        <FormField
                          label="CSS"
                          placeholder={t("Enter CSS")}
                          multiline
                          minRows={5}
                          onChange={(e) => {
                            updatePost("css", e.target.value);
                          }}
                          value={post.css}
                          disabled={mode === "view"}
                        />
                      )}
                      <SuiBox
                        p={3}
                        py={1.5}
                        style={{
                          background: colors.background.default,
                          margin: "0 -24px",
                          position: "relative",
                          fontSize: 12,
                          fontWeight: "bold",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          setExpandSeo(!expandSeo);
                        }}
                      >
                        {t("Search Engine Optimization")}
                        <SuiBox
                          p={2}
                          py={1.5}
                          style={{
                            position: "absolute",
                            right: 0,
                            top: 0,
                          }}
                        >
                          <SuiTypography>
                            <Icon sx={{ fontWeight: "bold" }}>
                              {expandSeo ? "expand_less" : "expand_more"}
                            </Icon>
                          </SuiTypography>
                        </SuiBox>
                      </SuiBox>
                      {expandSeo && (
                        <>
                          <FormField
                            label={t("Meta Title")}
                            placeholder={t("Enter Meta Title")}
                            onChange={(e) => {
                              updatePost("metaTitle", e.target.value);
                            }}
                            value={readPost("metaTitle")}
                            disabled={mode === "view"}
                          />
                          <FormField
                            label={t("Meta Keywords")}
                            placeholder={t("Enter Meta Keywords")}
                            multiline
                            minRows={5}
                            onChange={(e) => {
                              updatePost("metaKeyword", e.target.value);
                            }}
                            value={readPost("metaKeyword")}
                            disabled={mode === "view"}
                          />
                          <FormField
                            label={t("Meta Description")}
                            placeholder={t("Enter Meta Description")}
                            multiline
                            minRows={5}
                            onChange={(e) => {
                              updatePost("metaDescription", e.target.value);
                            }}
                            value={readPost("metaDescription")}
                            disabled={mode === "view"}
                          />
                        </>
                      )}
                    </Grid>
                  )}
                </SuiBox>
              </Card>
            </Grid>

            <Grid item xs={12} lg={3}>
              <TableContainer>
                <Table>
                  <TableHead style={{ borderBottom: "1px solid #ccc" }}>
                    <SuiBox component="tr" display="block" mb={1.5}>
                      <SuiBox component="td">
                        <SuiTypography variant="h5">
                          {t("Post Settings")}
                        </SuiTypography>
                        {isEditing && (
                          <SuiTypography variant="caption">
                            <SuiBadgeDot size="xs" color={getPostOriginalStatusColor()} badgeContent={t(getPostOriginalStatus())} />
                          </SuiTypography>
                        )}
                      </SuiBox>
                      {isEditing && mode !== "view" && (
                        <SuiBox component="td" width="10%">
                          <SuiButton onClick={del}
                            variant="outlined"
                            style={{ color: "#981ceb", padding: '5px' }}
                          >
                            {t("delete")}
                          </SuiButton>
                        </SuiBox>
                      )}
                    </SuiBox>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell style={{ border: 0 }}>
                        <SuiBox>
                          <SuiTypography component="label" variant="caption" fontWeight="bold">
                            {t("Publish Settings")}
                          </SuiTypography>
                          <SuiSelect
                            value={{
                              label: t(_.capitalize(getPostStatusLabel())),
                              value: "",
                            }}
                            options={getPostStatusOptions()}
                            onChange={(option) => {
                              updatePost("status", option.value);
                            }}
                            isDisabled={mode === "view"}
                          />
                          {post.status === "2" && (
                            <FormField
                              label={t("Publish Date")}
                              customInput={
                                <>
                                  <SuiDatePicker
                                    value={moment(scheduleDate).format("YYYY-MM-DD")}
                                    input={{
                                      icon: {
                                        component: "event",
                                        direction: "right",
                                      }
                                    }}
                                    options={{
                                      minDate: moment().format("YYYY-MM-DD"),
                                    }}
                                    onChange={(...vars) => {
                                      handleScheduleChange(vars[1], scheduleTime);
                                    }}
                                  />
                                  <SuiTypography component="label" variant="caption" fontWeight="bold">
                                    {t("Publish Time")}
                                  </SuiTypography>
                                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <TimePicker
                                      inputRef={timePickerRef}
                                      value={moment(scheduleTime, ["HH:mm:00"])}
                                      onChange={(newValue) => {
                                        handleScheduleChange(
                                          scheduleDate,
                                          moment(newValue).format("HH:mm:00")
                                        );
                                      }}
                                      renderInput={(params) => (
                                        <TextField {...params} style={{ width: "100%" }} />
                                      )}
                                      OpenPickerButtonProps={{
                                        style: {
                                          position: "absolute",
                                          right: 10,
                                        },
                                      }}
                                    />
                                  </LocalizationProvider>
                                </>
                              }
                            />
                          )}
                        </SuiBox>
                        {(postType.isSendPushNotification === 1 || postType.isSendPushNotification === 3) && (
                          <SuiBox>
                            <SuiTypography component="label" variant="caption" fontWeight="bold">
                              {t("Push Notification Setting")}
                            </SuiTypography>
                            <SuiBox ml={2} display="inline">
                              <Switch
                                disabled={true}//postType.isSendPushNotification !== 1 || mode === "view"}
                                checked={isSendPushNotification}
                                onChange={(v) => {
                                  updatePost("isSendPushNotification", v.currentTarget.checked);
                                  setIsSendPushNotification(v.currentTarget.checked);
                                }}
                              />
                            </SuiBox>
                          </SuiBox>
                        )}
                        <SuiBox mt={3} mb={3} display="flex" justifyContent="space-between">
                          <SuiBox mt={7} style={{ fontSize: 12 }} alignSelf="flex-end">
                            {post.updatedAt && (
                              <p>
                                {t("Last edit")}:{" "}
                                {moment(post.updatedAt).format("YYYY-MM-DD hh:mm:ss A")}
                              </p>
                            )}
                            {post.publishedDate && (
                              <p>
                                {t("Published on")}:{" "}
                                {moment(post.publishedDate).format("YYYY-MM-DD hh:mm:ss A")}
                              </p>
                            )}
                          </SuiBox>
                          {mode !== "view" && (
                            <SuiBox ml={2} mr={0} alignSelf="flex-end">
                              <SuiButton variant="gradient" color="info" onClick={save}>
                                {t("save")}
                              </SuiButton>
                            </SuiBox>
                          )}
                        </SuiBox>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </SuiBox>
        <Footer />
      </DashboardLayout>
    </>
  );
}

NewOrEditPost.defaultProps = {
  mode: "view",
};
NewOrEditPost.propTypes = {
  mode: PropTypes.string,
};

export default NewOrEditPost;
